import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SidebarHeader } from './header';
import { Search, SearchResults } from './search';
import { Conversations } from './conversations';
import { getConversations } from '../../features/chatSlice';

const STORAGE_KEY = 'sidebar_prefs';
const MIN_WIDTH = 240;
const MAX_WIDTH = 600;
const DEFAULT_WIDTH = 360;
const COLLAPSED_WIDTH = 52;

function loadPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        collapsed: !!p.collapsed,
        width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Number(p.width) || DEFAULT_WIDTH))
      };
    }
  } catch (_) {}
  return { collapsed: false, width: DEFAULT_WIDTH };
}

function savePrefs(collapsed, width) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ collapsed, width }));
  } catch (_) {}
}

export default function Sidebar({ onlineUsers, typing }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [searchResults, setSearchResults] = useState([]);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [platform, setPlatform] = useState('');
  const [aiEnabled, setAiEnabled] = useState('');
  const [hasUnread, setHasUnread] = useState('');
  const [source, setSource] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [prefs, setPrefs] = useState(loadPrefs);
  const [isResizing, setIsResizing] = useState(false);
  const collapsed = prefs.collapsed;
  const width = prefs.width;
  const setCollapsed = (fn) => setPrefs((p) => ({ ...p, collapsed: typeof fn === 'function' ? fn(p.collapsed) : fn }));
  const setWidth = (fn) => setPrefs((p) => ({ ...p, width: typeof fn === 'function' ? fn(p.width) : fn }));

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startW = width;

    const onMove = (ev) => {
      const isRtl = document.documentElement.dir === 'rtl';
      const dx = isRtl ? startX - ev.clientX : ev.clientX - startX;
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + dx));
      setWidth(next);
    };

    const onUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const toggleCollapse = () => setCollapsed((c) => !c);

  useEffect(() => {
    savePrefs(prefs.collapsed, prefs.width);
  }, [prefs.collapsed, prefs.width]);

  useEffect(() => {
    if (!user?.token) return;
    const handler = setTimeout(() => {
      const params = {
        search: search || undefined,
        platform: platform || undefined,
        aiEnabled: aiEnabled || undefined,
        hasUnread: hasUnread || undefined,
        source: source || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined
      };
      dispatch(getConversations({ token: user.token, params }));
    }, 350);
    return () => clearTimeout(handler);
  }, [dispatch, user?.token, search, platform, aiEnabled, hasUnread, source, dateFrom, dateTo]);

  const currentWidth = collapsed ? COLLAPSED_WIDTH : width;

  return (
    <div
      className="h-full select-none flex flex-shrink-0 relative dark:bg-dark_bg_2 border-e border-[#3D4852] overflow-hidden"
      style={{
        width: currentWidth,
        transition: isResizing ? 'none' : 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      {collapsed ? (
        <div className="w-full h-full flex flex-col items-center pt-4">
          <button
            onClick={toggleCollapse}
            className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-dark_hover_1 text-dark_svg_1 transition-colors rtl:rotate-180"
            title="توسيع الشريط الجانبي"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            <SidebarHeader onCollapse={toggleCollapse} />
            <Search
              value={search}
              onChange={setSearch}
              onSubmit={() => {}}
              filtersOpen={filtersOpen}
              onToggleFilters={() => setFiltersOpen((v) => !v)}
            />
            {filtersOpen && (
              <div className="px-[10px] pb-2">
                <div className="rounded-lg border border-dark_border_1 dark:bg-dark_bg_2 p-2 grid grid-cols-2 gap-2 text-sm">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-dark_text_2">المنصة</span>
                    <select className="input" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                      <option value="">الكل</option>
                      <option value="whatsapp">واتساب</option>
                      <option value="telegram">تيليجرام</option>
                      <option value="facebook">فيسبوك</option>
                      <option value="instagram">انستغرام</option>
                      <option value="web">ويب</option>
                      <option value="other">أخرى</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-dark_text_2">حالة الذكاء</span>
                    <select className="input" value={aiEnabled} onChange={(e) => setAiEnabled(e.target.value)}>
                      <option value="">الكل</option>
                      <option value="true">مفعّل</option>
                      <option value="false">معطّل</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-dark_text_2">غير مقروء</span>
                    <select className="input" value={hasUnread} onChange={(e) => setHasUnread(e.target.value)}>
                      <option value="">الكل</option>
                      <option value="true">نعم</option>
                      <option value="false">لا</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs text-dark_text_2">المصدر</span>
                    <select className="input" value={source} onChange={(e) => setSource(e.target.value)}>
                      <option value="">الكل</option>
                      <option value="customer">العميل</option>
                      <option value="assistant">الذكاء الاصطناعي</option>
                      <option value="admin">المشرف</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 col-span-1">
                    <span className="text-xs text-dark_text_2">من تاريخ</span>
                    <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1 col-span-1">
                    <span className="text-xs text-dark_text_2">إلى تاريخ</span>
                    <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </label>
                </div>
              </div>
            )}

            {searchResults.length > 0 ? (
              <SearchResults searchResults={searchResults} setSearchResults={setSearchResults} />
            ) : (
              <Conversations onlineUsers={onlineUsers} typing={typing} />
            )}
          </div>

          <div className="flex items-stretch">
            <div
              role="separator"
              onMouseDown={handleResizeStart}
              className="w-1.5 flex-shrink-0 cursor-col-resize hover:bg-green_1/30 transition-colors group"
              title="اسحب لتغيير الحجم"
            >
              <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-0.5 h-12 rounded-full bg-green_1" />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
